import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import Header from '../../../components/Header';
import ConfirmDialog from '../../../components/Geners/ConfirmationDialog';
import api_service from '../../../network/api_callbacks';

const STATUS_COLOR = {
  draft: 'default',
  published: 'success',
  closed: 'warning',
};

const publicUrl = (slug) => `${window.location.origin}/f/${slug}`;
const tokenUrlTemplate = (slug) => `${window.location.origin}/f/${slug}?token=<USER_ID_TOKEN>`;

export default function FeedbackFormsList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (search.trim()) params.search = search.trim();
      if (status) params.status = status;
      const response = await api_service.getFeedbackFormsList(params);
      if (response?.responseCode === 200) {
        setRows(response.data?.items || []);
        setPages(response.data?.pagination?.pages || 1);
      } else {
        toast.error(response?.error?.message || 'Failed to load forms');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const handleCreate = async () => {
    if (!createTitle.trim()) {
      toast.error('Title is required');
      return;
    }
    setCreating(true);
    try {
      const response = await api_service.createFeedbackForm({
        title: createTitle.trim(),
        status: 'draft',
        fields: [],
      });
      if (response?.responseCode === 201 || response?.responseCode === 200) {
        toast.success('Form created');
        setCreateOpen(false);
        setCreateTitle('');
        const formId = response.data?._id || response.data?.id;
        if (formId) navigate(`/feedback-forms/${formId}`);
        else loadForms();
      } else {
        toast.error(response?.error?.message || 'Failed to create form');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to create form');
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (form) => {
    try {
      const response = await api_service.publishFeedbackForm(form._id);
      if (response?.responseCode === 200) {
        toast.success('Form published');
        loadForms();
      } else {
        toast.error(response?.error?.message || 'Failed to publish');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to publish');
    }
  };

  const handleCloseForm = async (form) => {
    try {
      const response = await api_service.updateFeedbackForm({
        id: form._id,
        data: { status: 'closed' },
      });
      if (response?.responseCode === 200) {
        toast.success('Form closed');
        loadForms();
      } else {
        toast.error(response?.error?.message || 'Failed to close form');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to close form');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const response = await api_service.deleteFeedbackForm(deleteTarget._id);
      if (response?.responseCode === 200) {
        toast.success('Form deleted');
        setDeleteTarget(null);
        loadForms();
      } else {
        toast.error(response?.error?.message || 'Failed to delete form');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to delete form');
    }
  };

  const copyLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(publicUrl(slug));
      toast.success('Public URL copied');
    } catch {
      toast.error('Could not copy URL');
    }
  };

  const copyTokenLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(tokenUrlTemplate(slug));
      toast.success('Token URL template copied (replace <USER_ID_TOKEN>)');
    } catch {
      toast.error('Could not copy URL');
    }
  };

  return (
    <Box sx={{ m: '20px', mt: '100px', textAlign: 'left' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Header title="Feedback Forms" subtitle="Create, publish, and review dynamic feedback forms" />
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Create form
        </Button>
      </Box>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="published">Published</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
        </TextField>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Fields</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No feedback forms yet
                </TableCell>
              </TableRow>
            ) : (
              rows.map((form) => (
                <TableRow key={form._id} hover>
                  <TableCell>{form.title}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2">{form.slug}</Typography>
                      {form.status === 'published' ? (
                        <>
                          <Tooltip title="Copy guest public URL">
                            <IconButton size="small" onClick={() => copyLink(form.slug)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy form-only token URL template">
                            <IconButton size="small" onClick={() => copyTokenLink(form.slug)}>
                              <ContentCopyIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={form.status} color={STATUS_COLOR[form.status] || 'default'} />
                  </TableCell>
                  <TableCell>{form.fields?.length || 0}</TableCell>
                  <TableCell>
                    {form.updated_at ? new Date(form.updated_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton onClick={() => navigate(`/feedback-forms/${form._id}`)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Responses">
                      <IconButton onClick={() => navigate(`/feedback-forms/${form._id}/responses`)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {form.status !== 'published' ? (
                      <Tooltip title="Publish">
                        <IconButton onClick={() => handlePublish(form)}>
                          <PublishIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Close form">
                        <IconButton onClick={() => handleCloseForm(form)}>
                          <LockIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton color="error" onClick={() => setDeleteTarget(form)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pages > 1 ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination page={page} count={pages} onChange={(_, value) => setPage(value)} />
        </Box>
      ) : null}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create feedback form</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Title"
            margin="normal"
            value={createTitle}
            onChange={(e) => setCreateTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete form?"
        content={`Delete "${deleteTarget?.title || ''}"? Forms with responses cannot be deleted.`}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
